import React, { useEffect, useRef, useState } from "react";
import DocList from "../../components/DocAccessList";
import DocAccessibilityForm from "../../components/DocAccessibilityForm";
import { useLocation, useNavigate } from "react-router-dom";
import Header from "../../components/Header";
import SideBar from "../../components/Sidebar";
import { GET_ALL_ACCESSIBLE_DOCS } from "../../utils/constants";
import { makeAuthenticatedGet } from "../../utils/fetchUtils";
import { DocData } from "../../utils/types";
import useWindowWidth from "../../utils/windowWidth";
import AccessibilityHelperModal from "../../components/accessibilityHelper"; // New import

function DocAccess() {
  const [docs, setDocs] = useState<DocData[]>([]);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(5);
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [totalPages, setTotalPages] = useState(1);
  const [searchText, setSearchText] = useState("");
  const [displayMode, setDisplayMode] = useState<'convert' | 'previous'>('convert');

  const [show, setShow] = useState(false); // New state
  const handleClose = () => setShow(false); // New function
  const handleShow = () => setShow(true); // New function

  const navigate = useNavigate();
  const mainRef = useRef<HTMLInputElement>(null);
  const location = useLocation();

  function handleSearchText(event: any): void {
    setSearchText(event.target.value);
  }

  function handleSearchSubmit(event: any): void {
    event.preventDefault();
    console.log(searchText);
    const searchDocs = docs.filter((doc) => {
      return (doc.fileName.includes(searchText) || doc.tag?.includes(searchText));
    });
    setDocs(searchDocs);
  }

  const fetchDocs = async () => {
    setIsLoading(true);
    setMessage("");
    try {
      const url = GET_ALL_ACCESSIBLE_DOCS + `?page=${page}&limit=${limit}`;
      const response = await makeAuthenticatedGet(url);
      const temp = response;
      console.log("docs", temp);
      setDocs(temp.documents);
      setTotalPages(Math.floor((temp.totalBooks - 1) / limit) + 1);
    } catch (error: any) {
      if (error.response && error.response.data) {
        setMessage(error.response.data.message);
      }
      console.error("Error fetching books: ", error);
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    if (localStorage.getItem('token') === null || localStorage.getItem('token') === undefined) {
      navigate('/login');
    }
    document.title = 'Document Accessibility - Nclude Portal';

    // Set initial display mode based on URL
    if (location.pathname.includes('/docaccess/new')) {
      setDisplayMode('convert');
    } else {
      setDisplayMode('previous');
    }

    // Fetch docs only if in 'previous' mode
    if (displayMode === 'previous') {
      fetchDocs();
      const intervalId = setInterval(fetchDocs, 0.5 * 60 * 1000);
      return () => clearInterval(intervalId);
    }
  }, [navigate, location.pathname, displayMode, page, limit]);

  useEffect(() => {
    if (mainRef.current) {
      mainRef.current.focus();
    }
  }, [location]);

  const windowWidth = useWindowWidth();
  const isMobile = windowWidth < 769;

  const handleToggleToConvert = () => {
    setDisplayMode('convert');
    navigate('/docaccess/new');
  };

  const handleToggleToPrevious = () => {
    setDisplayMode('previous');
    navigate('/docaccess');
  };

  const handlePageChange = (nextPage: number) => {
    if (nextPage >= 1 && nextPage <= totalPages) setPage(nextPage);
  };

  return (
    <div className='containerr'>
      {!isMobile && <Header />}
      <div className="page-container">
        <SideBar activePage="docAccessibility" />
        <main className="doc-access-page doc-container" id="main-content" tabIndex={-1} style={{ paddingTop: isMobile ? "2rem" : "" }}>
          <h1 className="display-title" ref={mainRef} tabIndex={-1}>
            Document Accessibility
          </h1>

          {/* In-page toggles */}
          <div className="accessibility-header mb-8 md:mb-4 d-flex justify-content-between align-items-center">
            <div
              role="tablist"
              aria-label="Document Accessibility Options"
              className="d-flex w-100"
              style={{ gap: "10px" }}
            >
              <button
                type="button"
                role="tab"
                aria-selected={displayMode === 'convert'}
                onClick={handleToggleToConvert}
                className={`flex-fill ${displayMode === 'convert' ? "active-accessibility-header" : ""}`}
              >
                Convert Document
              </button>

              <button
                type="button"
                role="tab"
                aria-selected={displayMode === 'previous'}
                onClick={handleToggleToPrevious}
                className={`flex-fill ${displayMode === 'previous' ? "active-accessibility-header" : ""}`}
              >
                Previous Requests
              </button>
            </div>
          </div>

          {/* Conditional rendering of sub-components */}
          {displayMode === 'convert' ? (
            <>
              <section>
                <p className="accessibility-helper">Learn
                  {" "}
                  <button
                    type="button"
                    className="click"
                    onClick={handleShow}
                    aria-label="Learn how document accessibility works"
                    aria-haspopup="dialog"
                    aria-controls="accessibility-modal"
                    aria-expanded={show}
                  >
                    how document conversion works
                  </button>.</p>
                <AccessibilityHelperModal show={show} handleClose={handleClose} parent={'doc'} />
              </section>
              <section className="component-container form-container upload-form-container" aria-labelledby="formFileLabel">
                <DocAccessibilityForm />
              </section>
            </>
          ) : (
            <>
              {message && <p className="page-error" role="alert">{message}</p>}
              <div>
                <section className="doc-request-search search-div">
                  <form className="d-flex search-form" role="search" onSubmit={handleSearchSubmit}>
                    <label htmlFor="searchInput" className="visually-hidden">
                      Search document name or tags
                    </label>
                    <input
                      id="searchInput"
                      className="form-control me-2 search-input"
                      type="search"
                      placeholder="Search document name or tags..."
                      aria-label="Search document name or tags"
                      value={searchText}
                      onChange={handleSearchText}
                    />
                    <button className="btn search-btn" type="submit" aria-label="search document">
                      Search
                    </button>
                  </form>
                </section>
              </div>
              <section className="doc-request-results component-container" >
                <DocList
                  docList={docs}
                  page={page}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                  isLoading={isLoading}
                />
              </section>
            </>
          )}
        </main>
      </div>
    </div>
  );
}

export default DocAccess;
